import {
	Autocomplete,
	Grid,
	IconButton,
	Stack,
	TextField,
} from '@mui/material';
import { FullPage, FullPageWrapper } from '@Utils/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalization } from '@Translations/useGlobalization';
import {
	Button,
	InfoCard,
	NavigationPrompt,
	Text,
	useUnits,
} from '@innovyze/stylovyze';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	selectCurrentTree,
	selectNewTree,
	selectRehabActions,
	selectRehabTreesIsCreating,
} from '@Selectors';
import { Create } from '@mui/icons-material';
import {
	getMetadata,
	getMetadataResolved,
	setAttributes,
} from '@Actions/Risk.actions';
import {
	selectAttributes,
	selectAllUnitMatadata,
	selectIsAnyMetadataLoading,
} from '@Selectors/Risk.selectors';
import { Metadata, UnitMetadata, AttributeOption } from '@Types';
import { EditAttribute } from '@Components/EditAttribute';
import {
	addRehabTree,
	saveRehabTree,
	setCurrentTreeNodeActions,
	setNewTreeName,
	setRehabDeletedActions,
	setStartNodeId,
	setUpdateNewTree,
} from '@Actions/Rehab.actions';
import * as uuid from 'uuid';
import { buildTreeNodeActions, getAttributeLabel } from '@Utils/ags';
import { TFunc } from '@Translations/types';
import { MEASUREMENT_SYSTEM } from '@Utils/units/unitsMappingValues';

export const AutomaticSetupPanel = (
	forEdit: boolean,
	readOnly: boolean,
	allAttributes: AttributeOption[],
	disable: boolean,
	getSystemValueFormatted: (
		value: string,
		invalidValue?: string,
		decimalPlaces?: number,
		standardUnit?: string,
	) => string | undefined,
	t: TFunc,
	onEditAtribute?: (key: string) => void,
	onEditSetup?: () => void,
) => {
	return (
		<InfoCard
			title={
				forEdit ? (
					t('Select Attributes')
				) : (
					<Grid container xs={12}>
						<Grid item xs={readOnly ? 12 : 6}>
							{t('Automatic Setup')}
						</Grid>
						{!readOnly ? (
							<Grid
								item
								container
								xs={6}
								style={{ justifyContent: 'end' }}>
								<Button
									disabled={disable}
									variant="outlined"
									startIcon={<Create />}
									onClick={onEditSetup}>
									{t('Edit Automatic Setup')}
								</Button>
							</Grid>
						) : (
							[]
						)}
					</Grid>
				)
			}>
			<Grid container spacing={2}>
				{allAttributes.map(({ key, value, config }, index) => (
					<Grid
						item
						container
						xs={12}
						key={key}
						style={{ alignItems: 'center' }}
						columnSpacing={2}>
						<Grid item xs={4} sm={3} md={2}>
							{value}
						</Grid>
						<Grid item xs={8} sm={9} md={10}>
							<Stack direction={'row'} alignItems={'center'}>
								{config
									? config.label ??
									  getAttributeLabel(
											config.metadataType === 'range',
											t,
											getSystemValueFormatted,
											config.metadata,
											config.unitMetadata,
									  )
									: []}
								{forEdit && onEditAtribute ? (
									<IconButton
										disabled={disable}
										style={{ marginLeft: 16 }}
										title={t('Edit {{value}}', {
											value: value,
										})}
										data-cy={'editButton' + key}
										onClick={() => onEditAtribute(key)}>
										<Create />
									</IconButton>
								) : (
									[]
								)}
							</Stack>
						</Grid>
						<Grid xs={12} style={{ paddingTop: '6px' }}>
							{index < allAttributes.length - 1 ? (
								<hr
									style={{
										border: 'none',
										height: '1px',
										margin: 0,
										flexShrink: 0,
										backgroundColor: 'rgba(0, 0, 0, 0.12)',
									}}
								/>
							) : (
								[]
							)}
						</Grid>
					</Grid>
				))}
			</Grid>
		</InfoCard>
	);
};

const AutomaticSetup = () => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const {
		getSystemValueNoIntlFormat,
		getSystemValueFormatted,
		getStandardisedValue,
		getSystemUnit,
		system,
	} = useUnits();

	const currentTree = selectCurrentTree();
	const rehabActions = selectRehabActions();
	const newTree = selectNewTree();
	const isCreating = selectRehabTreesIsCreating();
	const isMetadataLoading = selectIsAnyMetadataLoading();
	const allUnitMetadata = selectAllUnitMatadata();
	const allAttributes = selectAttributes();

	const [isDirty, setIsDirty] = useState<boolean>(false);
	const [edit, setEdit] = useState<boolean>(false);
	const [assetType, setAssetType] = useState<string>('wwPipe');
	const [validConfig, setValidConfig] = useState<boolean>(false);
	const [enableSelectUpdate, setEnableSelectUpdate] =
		useState<boolean>(false);
	const [showSelected, setShowSelected] = useState<boolean>(false);
	const [selectedAttributes, setSelectedAttributes] = useState<
		AttributeOption[]
	>([]);

	const [editAttributes, setEditAttributes] = useState<
		AttributeOption | undefined
	>();

	// Check we have a AGS to edit/create
	useEffect(() => {
		let systemType, assetType;
		if (currentTree.name !== '') {
			setEdit(true);
			systemType = currentTree.systemType;
			assetType = currentTree.assetType;
		} else if (newTree.name !== '') {
			setEdit(false);
			systemType = newTree.systemType;
			assetType = newTree.assetType;
			setIsDirty(true);
		}

		// Translate the system + type into the STANDARD (!!!) asset type
		if (systemType && assetType) {
			switch (systemType?.toLowerCase()) {
				case 'sanitary sewer':
					switch (assetType?.toLowerCase()) {
						case 'manhole':
							setAssetType('wwManhole');
							break;
						case 'pipe':
						default:
							setAssetType('wwPipe');
							break;
					}
					break;
				case 'water distribution':
					switch (assetType?.toLowerCase()) {
						case 'pipe':
						default:
							setAssetType('pipe');
							break;
					}
					break;
				default:
					history.replace('/agsets');
					break;
			}
		} else {
			history.replace('/agsets');
		}
	}, []);

	// TODO - These are hardcoded for the MVP
	const attributeOptions: AttributeOption[] = useMemo(() => {
		switch (assetType) {
			case 'wwPipe':
				return [
					{ key: 'material', value: t('Material') },
					{ key: 'width', value: t('Width') },
					{
						key: 'yearlaid',
						value: t('Year Laid'),
						dbFieldName: 'YEAR_LAID',
					},
				];
			case 'pipe':
				return [
					{
						key: 'material',
						value: t('Material'),
						dbFieldName: 'MATERIAL',
					},
					{
						key: 'nominalDiameter',
						value: t('Nominal Diameter'),
						dbFieldName: 'NOMINAL_DIAMETER',
					},
					{
						key: 'dateInstalled',
						value: t('Date Installed'),
						dbFieldName: 'DATE_INSTALLED',
					},
				];
		}

		return [];
	}, [assetType]);

	useEffect(() => {
		// Editing an existing config ?
		if (edit && currentTree.config) {
			const selected: AttributeOption[] = [];
			currentTree.config.attributes.forEach(attribute => {
				// Find the selected attributed
				const found = attributeOptions.find(
					option => option.key === attribute.key,
				);
				if (found) {
					selected.push(found);

					// Now the extract the configuration
					dispatch(
						getMetadataResolved({
							key: attribute.config?.unitMetadataName ?? '',
							value: attribute.config?.unitMetadata,
						}),
					);
				}
			});

			setSelectedAttributes(selected);
			dispatch(setAttributes(currentTree.config.attributes));
			setShowSelected(true);
			setIsDirty(false);
		}
	}, [edit, attributeOptions]);

	const handleAttirbuteInputChange = (
		_event: React.SyntheticEvent,
		values: AttributeOption[],
	) => {
		if (values.length !== 0) {
			setEnableSelectUpdate(true);
			setSelectedAttributes([...values]);
		} else {
			setEnableSelectUpdate(false);
		}
	};

	const fetchMetadata = () => {
		// Find a selected option that's not already downloaded
		const option = selectedAttributes.find(selected => {
			return (
				allUnitMetadata[`${assetType}-${selected.key}`] === undefined
			);
		});

		if (option) {
			dispatch(
				getMetadata({
					assetType: assetType,
					field: option.key,
					fieldAlias: option.value,
				}),
			);
		} else {
			// Nothing missing so ensure that they selected ones are now visible
			const attributes: AttributeOption[] = selectedAttributes.map(
				selected => {
					// Find the related metadata
					let attributes = allAttributes.find(
						attribute => attribute.key === selected.key,
					);
					if (!attributes) {
						const unitMetadataName = `${assetType}-${selected.key}`;
						const unitMetadata = allUnitMetadata[unitMetadataName];
						if (unitMetadata) {
							const config = createDefaulConfig(
								unitMetadata,
								unitMetadataName,
								selected.key === 'width' ||
									selected.key === 'diameter',
							);

							attributes = {
								...selected,
								config: config,
							};
						} else {
							attributes = { ...selected, config: undefined };
						}
					}
					return attributes;
				},
			);

			dispatch(setAttributes(attributes));
		}
	};

	const createDefaultRangeValues = (
		newRanges: number,
		unitMetadata?: UnitMetadata,
	): Metadata[] | undefined => {
		let metadata = undefined;
		if (unitMetadata) {
			if (unitMetadata.range) {
				metadata = new Array(newRanges + 2);
				const defaultScore: number[] = [metadata.length];

				let rangeMin = unitMetadata.range.min;
				if (rangeMin == null || rangeMin == undefined) {
					rangeMin = 0;
				}

				let rangeMax = unitMetadata.range.max;
				if (rangeMax == null || rangeMax == undefined) {
					rangeMax = 0;
				}

				// Do we have to care about units at this point?
				if (unitMetadata.unit && system !== MEASUREMENT_SYSTEM.METRIC) {
					const precision: number = unitMetadata.precision ?? 0;
					// To make the ranges work we need to do a set in native unit and one in display units
					// Otherwise the initial display can have too many decimal places until the user edits a value
					const internalUnit = getSystemUnit(unitMetadata.unit);
					const adjustment = unitMetadata.unit === 'mm' ? 10 : 1;
					const unitMin = parseFloat(
						getSystemValueNoIntlFormat(
							`${rangeMin} ${unitMetadata.unit}`,
						),
					);
					const unitMax = parseFloat(
						getSystemValueNoIntlFormat(
							`${rangeMax} ${unitMetadata.unit}`,
						),
					);

					const unitRange = unitMax - unitMin;

					let interval = unitRange / newRanges;
					// If the interval is 0 then make it the minimum increment
					if (interval === 0) {
						interval = +(1 / Math.pow(10, precision)).toFixed(
							precision,
						);
					} else {
						interval = +interval.toFixed(precision);
					}

					let first = unitMin;
					let nativeFirst = rangeMin;
					let pos = 0;

					defaultScore[pos] = nativeFirst;
					metadata[pos] = {
						key: ` <= ${nativeFirst.toFixed(precision)}`,
						value: 0,
						ix: pos++,
					};

					for (let i = 0; i < newRanges - 1; i++) {
						const next = +(first + interval).toFixed(precision);
						const nativeNext =
							parseFloat(
								getStandardisedValue(`${next} ${internalUnit}`),
							) * adjustment;

						metadata[pos] = {
							key: `${nativeFirst.toFixed(
								precision,
							)} <= ${nativeNext.toFixed(precision)}`,
							value: 0,
							ix: pos++,
						};
						defaultScore[pos] = nativeNext;
						nativeFirst = nativeNext;
						first = next;
					}

					// Ignore the range max as it could be less than we need to make a unique range
					const max = first + interval;
					const nativeMax =
						parseFloat(
							getStandardisedValue(`${max} ${internalUnit}`),
						) * adjustment;

					defaultScore[pos] = nativeMax;
					metadata[pos] = {
						key: `${nativeFirst.toFixed(
							precision,
						)} <= ${nativeMax.toFixed(precision)}`,
						value: 0,
						ix: pos++,
					};

					defaultScore[pos] = nativeMax;
					metadata[pos] = {
						key: `> ${nativeMax.toFixed(precision)}`,
						value: 0,
						ix: pos++,
					};
				} else {
					const precision: number = unitMetadata.precision ?? 0;
					const range = rangeMax - rangeMin;
					let interval = range / newRanges;
					// If the interval is 0 then make it the minimum increment
					if (interval === 0) {
						interval = +(1 / Math.pow(10, precision)).toFixed(
							precision,
						);
					} else {
						interval = +interval.toFixed(precision);
					}

					let first = rangeMin;
					let pos = 0;

					defaultScore[pos] = first;
					metadata[pos] = {
						key: ` <= ${first.toFixed(precision)}`,
						value: 0,
						ix: pos++,
					};

					for (let i = 0; i < newRanges - 1; i++) {
						const next = +(first + interval).toFixed(precision);
						metadata[pos] = {
							key: `${first.toFixed(precision)} <= ${next.toFixed(
								precision,
							)}`,
							value: 0,
							ix: pos++,
						};
						defaultScore[pos] = next;
						first = next;
					}

					// Ignore the range max as it could be less than we need to make a unique range
					const max = first + interval;
					defaultScore[pos] = max;
					metadata[pos] = {
						key: `${first.toFixed(precision)} <= ${max.toFixed(
							precision,
						)}`,
						value: 0,
						ix: pos++,
					};

					defaultScore[pos] = max;
					metadata[pos] = {
						key: `> ${max.toFixed(precision)}`,
						value: 0,
						ix: pos++,
					};
				}
			}
		}
		return metadata;
	};

	const createDefaulConfig = (
		unitMetadata: UnitMetadata,
		unitMetadataName: string,
		forWidth: boolean,
	) => {
		if (unitMetadata.range) {
			let metadata: Metadata[] | undefined;
			if (forWidth) {
				if (system !== MEASUREMENT_SYSTEM.METRIC) {
					metadata = [
						{
							key: ' <= 406.4',
							value: 0,
							ix: 0,
						},
						{
							key: '406.4 <= 914.4',
							value: 0,
							ix: 0,
						},
						{
							key: '> 914.4',
							value: 0,
							ix: 0,
						},
					];
				} else {
					metadata = [
						{
							key: ' <= 400',
							value: 0,
							ix: 0,
						},
						{
							key: '400 <= 900',
							value: 0,
							ix: 0,
						},
						{
							key: '> 900',
							value: 0,
							ix: 0,
						},
					];
				}
			} else {
				// Process the range values
				metadata = createDefaultRangeValues(3, unitMetadata);
			}

			if (metadata) {
				// Don't bother with a label since it may need unit conversion before display
				return {
					label: undefined,
					metadata: metadata,
					metadataType: 'range',
					unitMetadataName: unitMetadataName,
					unitMetadata: unitMetadata,
				};
			}
		} else if (unitMetadata.unique && unitMetadata?.unique?.length > 0) {
			// Process the unique values
			const metadata = unitMetadata.unique.map(item => ({
				key: item,
				value: 0,
			}));

			return {
				label: unitMetadata.unique.join(', '),
				metadata: metadata,
				metadataType: 'unique',
				unitMetadataName: unitMetadataName,
				unitMetadata: unitMetadata,
			};
		}
	};

	useEffect(() => {
		fetchMetadata();
	}, [allUnitMetadata]);

	const onSelectUpdate = () => {
		// New selection
		if (!showSelected) {
			setShowSelected(true);
		}
		fetchMetadata();
		setEnableSelectUpdate(false);
		setIsDirty(true);
	};

	const onCreateUpdate = () => {
		if (allAttributes.length > 0) {
			const treeId = edit ? currentTree._id : uuid.v4();
			const nodeActions = buildTreeNodeActions(
				treeId,
				allAttributes,
				t,
				getSystemValueFormatted,
			);

			if (edit) {
				// Set the existing actions to be deleted
				const rehabDeletedActions = rehabActions.map(action => {
					return action._id;
				});
				// Update the existing tree and overwrite it with the new actions and nodes
				dispatch(
					setUpdateNewTree({
						name: currentTree.name,
						description: currentTree.description,
						systemType: currentTree.systemType || 'Sanitary Sewer',
						assetType: currentTree.assetType || 'pipe',
						treeNodes: nodeActions.treeNodes,
						inspectionStandard: currentTree.inspectionStandard,
						mode: currentTree.mode,
						config: { attributes: allAttributes },
						groupCount: nodeActions.actions.length,
						treeType: currentTree.treeType,
						actions: nodeActions.actions,
					}),
				);
				dispatch(setRehabDeletedActions(rehabDeletedActions));
				dispatch(setStartNodeId(nodeActions.startNodeId));
				dispatch(setCurrentTreeNodeActions(nodeActions));

				setIsDirty(false);
				// Now save it on the server
				dispatch(
					saveRehabTree({
						treeId: treeId,
						history: history,
						historyReplace: true,
					}),
				);
			} else {
				// Update and create the new tree
				dispatch(
					setNewTreeName({
						...newTree,
						config: { attributes: allAttributes },
						groupCount: nodeActions.actions.length,
						createWithId: treeId,
						createWithTreeNodes: nodeActions.treeNodes,
						actions: nodeActions.actions,
					}),
				);
				setIsDirty(false);
				// Now save and create it on the server
				dispatch(
					addRehabTree({
						history: history,
						runTree: true,
						historyReplace: true,
					}),
				);
			}
		}
	};

	const onCancel = () => {
		history.goBack();
	};

	const title = useMemo(() => {
		if (edit) return t('Edit Automatic Setup');
		else return t('Automatic Setup');
	}, [edit]);

	const secondary = () => {
		return (
			<div>
				<Button
					variant="contained"
					color="secondary"
					style={{ marginRight: 16 }}
					disabled={isMetadataLoading || isCreating}
					onClick={() => {
						onCancel();
					}}>
					{t('Cancel')}
				</Button>
				<Button
					variant="contained"
					color="primary"
					disabled={
						!isDirty ||
						!validConfig ||
						isMetadataLoading ||
						isCreating
					}
					onClick={onCreateUpdate}>
					{edit ? t('Save') : t('Create Group')}
				</Button>
			</div>
		);
	};

	const onEditAtribute = (key: string) => {
		const attributes = allAttributes.find(
			attribute => attribute.key === key,
		);

		if (attributes && attributes.config && attributes.config.metadata) {
			// Load up the attributeDataGrid
			// if (attributes.config.metadataType == 'range') {
			// 	attributes.config.metadata

			// } else {

			// }

			setEditAttributes(attributes);
			setIsDirty(true);
		}
	};

	const onEditAtributeClose = () => {
		setEditAttributes(undefined);
	};

	const usedAttibutesPanel = useMemo(() => {
		if (allAttributes.length !== 0) {
			return AutomaticSetupPanel(
				true,
				false,
				allAttributes,
				isMetadataLoading || isCreating,
				getSystemValueFormatted,
				t,
				onEditAtribute,
				undefined,
			);
		} else {
			return [];
		}
	}, [allAttributes, isMetadataLoading, isCreating]);

	const usedAttibutesFooter = useMemo(() => {
		if (allAttributes.length !== 0) {
			let predictedGroups = 1;
			allAttributes.forEach(attribute => {
				if (attribute.config && attribute.config.metadata) {
					predictedGroups *= attribute.config.metadata.length;
				}
			});

			// Add the everthing else group
			predictedGroups++;

			setValidConfig(true);
			return (
				<Text variant="text-medium">
					{t('You will have {{groups}} asset groups.', {
						groups: predictedGroups,
					})}
				</Text>
			);
		} else {
			setValidConfig(false);
			return [];
		}
	}, [allAttributes]);

	return (
		<FullPageWrapper
			key={Date.now()}
			title={title}
			applyPadding={true}
			headerUnderline
			superHeader={t('Asset Group Sets')}
			className={isMetadataLoading || isCreating ? 'wait' : ''}
			secondary={secondary()}>
			<FullPage>
				<NavigationPrompt
					when={isDirty}
					navigate={nextLocation => {
						history.push(nextLocation);
					}}
					confirmText={t('Discard')}
					shouldBlockNavigation={() => true}
					shouldBlockReload={isDirty}
					content={
						edit
							? t(
									'You have modified your configuration and have unsaved changes. You can discard changes, or cancel to continue editing.',
							  )
							: t(
									'You have not finished creating your Asset Group Set. You can discard the configuration, or cancel to continue.',
							  )
					}></NavigationPrompt>

				<EditAttribute
					key={Date.now()}
					option={editAttributes}
					onClose={onEditAtributeClose}
					assetType={assetType}
					createDefaultRangeValues={createDefaultRangeValues}
				/>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Text variant="heading-medium-strong">
							{t('Select Attributes')}
						</Text>
						<Text variant="text-medium">
							{t(
								'We’ll group your assets using the selected attributes.',
							)}
						</Text>
						<Text variant="text-medium">
							{t(
								'The more attributes you select, the more asset groups you will have.',
							)}
						</Text>
					</Grid>
					<Grid item xs={10} sm={9} md={8} lg={7} xl={6}>
						<Autocomplete
							multiple
							options={attributeOptions}
							value={selectedAttributes}
							getOptionLabel={option => option.value}
							renderInput={params => (
								<TextField {...params} variant="outlined" />
							)}
							onChange={handleAttirbuteInputChange}
						/>
					</Grid>
					<Grid
						item
						xs={2}
						sm={3}
						md={4}
						lg={5}
						xl={6}
						style={{ alignContent: 'center' }}>
						<Button
							variant="outlined"
							color="primary"
							disabled={
								!enableSelectUpdate ||
								isMetadataLoading ||
								isCreating
							}
							onClick={onSelectUpdate}>
							{showSelected ? t('Update') : t('Select')}
						</Button>
					</Grid>
					<Grid item xs={12}>
						{showSelected ? usedAttibutesPanel : []}
					</Grid>
					<Grid item xs={12}>
						{showSelected ? usedAttibutesFooter : []}
					</Grid>
				</Grid>
			</FullPage>
		</FullPageWrapper>
	);
};
export default AutomaticSetup;
